/**
 * V37.42.1 — Client-side mode heuristic (mirrors backend deterministic priority).
 * V37.44.36 — Added pivot-verb + direct-object proximity analysis so that
 *             prompts like "build a bug fix app …" / "create a crash
 *             reporting dashboard …" are no longer locked to bug_fixing
 *             purely because a bug keyword appears in the app's NAME,
 *             DOMAIN, or PURPOSE. This mirrors the backend V37.44.33
 *             pivot-proximity + V37.44.35 arbiter-honors-advisory fixes.
 *
 * Pure UI-only utility. Eliminates the brief New Project → Bug Fix UI flicker
 * that occurs when the URL hint disagrees with the backend's resolved mode
 * (e.g., URL `mode=new_project` but user typed "Fix the login button").
 *
 * Strategy: deterministic, content+context aware precedence:
 *   1. Failure flag → bug_fixing (locked)
 *   2. Negated bug ("no bugs") → new_project
 *   3. Pivot verb + specific new_project object within 60-char window →
 *      new_project (BEATS raw BUG_INTENT_RE so distant bug keywords in
 *      app name/purpose do not hijack the classification)
 *   4. Bug intent keyword → bug_fixing
 *   5. Project intent regex → new_project
 *   6. null — undecided; caller falls back to URL hint or default
 *
 * The backend's `mode_resolved` SSE event remains authoritative — if the
 * heuristic is wrong, the existing flip handler in useStreamingPipeline
 * corrects it. Heuristic just minimizes the window where the wrong UI is
 * visible.
 *
 * Constraints honored: pure-sync (<1ms), no I/O, no LLM, no new deps,
 * additive only.
 */

// Mirrors backend `_BUG_INTENT_RE` (prompt_mode_detector.py).
const BUG_INTENT_RE = /\b(fix|fixing|fixes|debug|debugging|resolve|resolving|repair|repairing|patch|patching|bug|bugs|issue|issues|error|errors|crash|crashes|crashing|crashed|broken|break|breaks|not\s+working|fails?|failing|failure|exception|exceptions|traceback|stuck|hang|hanging|hung|timeout|timeouts|problem|problems|regress|regression|regressing)\b/i;

// Mirrors `_NEGATED_BUG_RE`.
const NEGATED_BUG_RE = /\b(no|without|free\s+of)\s+(bug|bugs|error|errors|issue|issues|problem|problems|crash|crashes|exception|exceptions)\b/i;

// Mirrors backend `intent_normalizer.py` FAILURE_FLAG patterns (subset).
const FAILURE_FLAG_RE = /\b(unresponsive|not\s+clickable|not\s+responding|cannot\s+(click|tap|press|type)|unable\s+to\s+(click|tap|press|type|navigate)|cannot\s+type|inability\s+to\s+type|not\s+displayed|missing\s+(ui|element|button|icon|label|field)|blank\s+(screen|page|view)|does\s+not\s+(render|appear|show|display|load))\b/i;

// Mirrors backend `_PROJECT_INTENT_PATTERNS` (subset).
const PROJECT_INTENT_RE = /\b(?:(?:build|create|scaffold|develop|design|implement|launch|deploy|architect|prototype)\s+(?:a|an|the|new|my|our|your)\s+(?!fix\b|patch\b|hotfix\b|workaround\b|repro(?:duction)?\b|rollback\b|revert\b|bugfix\b)[a-z]|from\s+scratch|i\s+(?:want|need|would\s+like)\s+to\s+(?:build|create|develop|design|launch|scaffold|implement|prototype)|let'?s\s+(?:build|create|develop|design|launch)|\bmvp\b|\bboilerplate\b|\bgreenfield\b)/i;

// V37.44.36 — Pivot verbs we expect to introduce a direct object.
// Mirrors the backend `PIVOT_VERBS` keys in `prompt_mode_detector.py`.
const PIVOT_VERBS = [
  'build', 'create', 'scaffold', 'develop', 'design', 'implement',
  'launch', 'deploy', 'architect', 'prototype', 'make', 'generate',
  'produce', 'ship', 'write',
];
// Specific new_project objects (NOT generic articles). "new " is kept
// because it is semantically concrete. Must mirror the SPECIFIC subset
// of the backend's new_project_objects.
const NEW_PROJECT_SPECIFIC_OBJECTS = [
  'new ', 'app', 'application', 'website', 'site', 'platform', 'system',
  'service', 'feature', 'architecture', 'schema', 'component', 'module',
  'from scratch', 'pipeline', 'infrastructure', 'tool', 'dashboard',
  'portal', 'mvp', 'startup', 'saas', 'extension', 'plugin', 'widget',
  'marketplace', 'bot', 'chatbot', 'landing page', 'admin panel',
  'framework', 'sdk', 'cms', 'crm', 'erp', 'mobile app', 'web app',
  'desktop app',
];
// Specific bug_fix objects that bind the verb to a bug_fix meaning.
const BUG_FIX_SPECIFIC_OBJECTS = [
  'is failing', 'is broken', 'fails', 'failed', 'timeout', 'breaking',
  'not working', 'patch', 'hotfix', 'rollback', 'revert', 'bugfix',
  'the bug', 'this bug', 'that bug', 'the issue', 'this issue',
  'that issue', 'the error', 'this error', 'that error',
];
const PROXIMITY_WINDOW = 60;

/**
 * V37.44.36 — pivot-verb + direct-object proximity analysis.
 * Mirrors backend `_analyze_pivot_verb` (V37.44.33).
 *
 * Returns 'new_project', 'bug_fixing', or null.
 */
function pivotVerbProximity(text) {
  const lower = text.toLowerCase();
  for (const verb of PIVOT_VERBS) {
    let searchFrom = 0;
    // Scan every occurrence of the verb (not just the first).
    while (searchFrom < lower.length) {
      const pos = lower.indexOf(verb, searchFrom);
      if (pos === -1) break;

      // Whole-word verb boundary — reject "build" inside "buildable",
      // "rebuild", "builder", etc.
      const prev = pos > 0 ? lower.charAt(pos - 1) : ' ';
      const endPos = pos + verb.length;
      const next = endPos < lower.length ? lower.charAt(endPos) : ' ';
      const isAlnum = (c) => /[a-z0-9]/.test(c);
      if (isAlnum(prev) || isAlnum(next)) {
        searchFrom = endPos;
        continue;
      }

      // Direct-object slot: the next PROXIMITY_WINDOW chars.
      const slot = lower.slice(endPos, endPos + PROXIMITY_WINDOW);

      // Earliest match wins. Both lists are specific-semantic — no
      // generic articles — so no article-vs-noun tie-breaking needed.
      let bestPos = Infinity;
      let bestMode = null;
      for (const obj of BUG_FIX_SPECIFIC_OBJECTS) {
        const p = slot.indexOf(obj);
        if (p !== -1 && p < bestPos) { bestPos = p; bestMode = 'bug_fixing'; }
      }
      for (const obj of NEW_PROJECT_SPECIFIC_OBJECTS) {
        const p = slot.indexOf(obj);
        if (p !== -1 && p < bestPos) { bestPos = p; bestMode = 'new_project'; }
      }
      if (bestMode) return bestMode;

      searchFrom = endPos;
    }
  }
  return null;
}

/**
 * Detect mode from user input using the backend's deterministic precedence:
 *   1. Failure flag → bug_fixing (locked)
 *   2. Negated bug ("no bugs") → new_project
 *   3. V37.44.36 pivot-verb proximity → the verb's direct object decides
 *   4. Bug intent keyword → bug_fixing
 *   5. Project intent regex → new_project
 *   6. null (undecided)
 */
export function detectModeHeuristic(userInput) {
  if (!userInput || typeof userInput !== 'string') return null;
  const text = userInput.slice(0, 10000); // cap to match backend gate

  if (FAILURE_FLAG_RE.test(text)) return 'bug_fixing';
  if (NEGATED_BUG_RE.test(text)) return 'new_project';

  // V37.44.36 — pivot-verb proximity (content+context) runs BEFORE the
  // raw BUG_INTENT_RE check so that "build a bug fix app …" and friends
  // are correctly classified by the verb's direct object instead of
  // being locked to bug_fixing by a distant keyword in the app name or
  // domain description.
  const pivot = pivotVerbProximity(text);
  if (pivot) return pivot;

  if (BUG_INTENT_RE.test(text)) return 'bug_fixing';
  if (PROJECT_INTENT_RE.test(text)) return 'new_project';
  return null;
}

/**
 * Resolve the initial UI mode by combining user input heuristic with URL hint.
 * Bug-first precedence: any bug signal in input wins over a stale `new_project`
 * URL hint (matches backend V37.40.1 strict bug-first behavior).
 */
export function resolveInitialMode(userInput, urlMode) {
  const heuristic = detectModeHeuristic(userInput);
  if (heuristic === 'bug_fixing') return 'bug_fixing';
  // Project heuristic is only allowed to stand if URL didn't explicitly
  // pick bug_fixing. Otherwise prefer URL (user's explicit choice).
  if (heuristic === 'new_project' && urlMode !== 'bug_fixing') return 'new_project';
  if (urlMode === 'bug_fixing' || urlMode === 'new_project') return urlMode;
  return heuristic; // may be null; caller defaults later
}

export default { detectModeHeuristic, resolveInitialMode };
